import { inject, Injectable } from '@angular/core';
import { ParamMap, Router } from '@angular/router';
import { catchError, from, map, Observable, of, switchMap, take } from 'rxjs';
import { IUserInfo, Products } from 'src/app/interfaces/state-interface';
import { StoreService } from 'src/app/services/store/store.service';
import { API_CONSTANTS, API_ERRORS } from 'src/app/static/api.constant';
import { DataService } from '../data/data.service';
import { RequestParam } from 'src/app/interfaces/requestParam';
import { isArray as _isArray } from 'lodash-es';
import { ServerResponse } from 'src/app/interfaces/serverResponse';
import { Token } from 'src/app/app.model';
import { AuthService } from '../auth/auth.service';
import { RedirectionService } from '../redirection/redirection.service';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { HttpHeaders } from '@angular/common/http';
import { getRolesFromToken } from 'src/app/shared/jwt.helper';
import { normalizeEmail } from 'src/app/shared/email.helper';

@Injectable({
  providedIn: 'root',
})
export class SignupService {
  private dataService = inject(DataService);
  private store = inject(StoreService);
  private redirect = inject(RedirectionService);
  private authService: AuthService = inject(AuthService);

  getCountryCodes(): Observable<any> {
    const headerMaps = new Map();
    headerMaps.set('skip', 'true');
    const requestParam: RequestParam = {
      url: API_CONSTANTS.COUNTRIES,
      headers: Object.fromEntries(headerMaps),
    };

    return this.dataService.get(requestParam).pipe(
      take(1),
      map((response: ServerResponse) => {
        if (_isArray(response)) {
          let usCountry;
          const allCountries = response
            .filter((value) => {
              if (value.code === 'US') {
                usCountry = value;
              }
              return value.code != 'US';
            })
            .map((v, i) => ({ ...v, id: i }));
          allCountries.unshift(usCountry);
          return allCountries;
        } else {
          console.error('Unexpected response format:', response);
          return [];
        }
      }),
      catchError((error) => {
        console.error('Error fetching country codes:', error);
        return of([]);
      })
    );
  }

  registerUser(model: IUserInfo): Observable<any> {
    try {
      if (!model) {
        return of(null);
      }
      const userInfo = {
        firstName: model.firstName,
        lastName: model.lastName,
        email: normalizeEmail(model.email),
        company: {
          name: (model.company as unknown as string)?.trim(),
          productSigningUpFor: model.productSigningUpFor,
          annualRevenue: model.annualRevenue,
        },
      };

      const headerMaps = new Map();
      headerMaps.set('skip', 'true');

      const requestParam: RequestParam = {
        url: API_CONSTANTS.REGISTER,
        data: userInfo,
        params: { productType: this.store.getProduct() },
        headers: Object.fromEntries(headerMaps),
      };

      return this.dataService.post(requestParam).pipe(
        map((response) => {
          return { ...response.data, success: true };
        }),
        catchError((err) => {
          const errors = err?.error?.errors;
          const errorObj =
            Array.isArray(errors) && !!errors.length
              ? errors[0]
              : API_ERRORS.defaultError;
          return of({ ...err, success: false, ...errorObj });
        })
      );
    } catch (e) {
      console.log(e);
      return of(null);
    }
  }

  getUserInfo(userId: number): Observable<any> {
    try {
      const requestParam: RequestParam = {
        url: API_CONSTANTS.USERINFO(userId.toString()),
      };
      // const userInfo = await this.dataService.get(requestParam);
      // console.log('getUserInfo',userInfo);
      // return userInfo;

      return this.dataService.get(requestParam).pipe(
        switchMap((response) => {
          if (response.data) {
            const userInfo: IUserInfo = response.data;
            this.store.setUser(userInfo);
            return of({ data: response.data, success: true });
          }
          throw response;
        }),
        catchError((err) => {
          return of({
            error: err,
            success: false,
            data: null,
            errors: this.dataService.getError(err),
          });
        })
      );
    } catch (e) {
      console.log(e);
      return of({ errors: this.dataService.getError(e) });
    }
  }
  setPassword(code: string, userId: string, password: string, firstName?: string, lastName?: string) {
    try {
      const headerMaps = new Map();
      headerMaps.set('skip', 'true');
      const requestParam: RequestParam = {
        url: API_CONSTANTS.SET_PASSWORD(code),
        params: (!!firstName && !!lastName) ? { userId, password, firstName, lastName } : { userId, password }, // {firstName, lastName}
        headers: Object.fromEntries(headerMaps),
      };
      return this.dataService.put(requestParam).pipe(
        map((response) => {
          console.log(response);
          const errors = this.dataService.getError(response);
          if (response && !response.data && errors) {
            return { ...response, success: false, errors };
          }
          return { ...(response?.data || {}), success: true };
        }),
        catchError((err) => {
          // const errors = err?.error?.errors;
          // const errorObj = Array.isArray(errors) && !!errors.length ? errors[0] : API_ERRORS.defaultError;
          // console.log(errorObj);
          return of({
            ...err,
            success: false,
            errors: this.dataService.getError(err),
          });
        })
      );
    } catch (e) {
      console.log(e);
      return of({ success: false, ...API_ERRORS.defaultError });
    }
  }

  verifyFirebaseToken(
    firetoken: string,
    customRedirectionUrl: string = ''
  ): Observable<any> {
    const requestParam: RequestParam = {
      url: API_CONSTANTS.VERIFY_TOKEN,
      params: {
        firebasetoken: firetoken,
        productType:
          this.store.getProduct() === Products.IQ
            ? Products.PULSE
            : this.store.getProduct(),
      },
    };
    // return this.dataService.post(requestParam).toPromise();
    return this.dataService.post(requestParam).pipe(
      switchMap(async (response) => {
        const errors = this.dataService.getError(response);
        const token = response?.data?.token || null;
        const roles = token ? getRolesFromToken(token) : null;

        if (response.data && (roles || !!roles?.length)) {
          const saras_token = response.data;
          const token = new Token();
          token.role = saras_token.role;
          token.token = saras_token.token;
          token.userId = saras_token.userId;
          this.store.setToken(token);
          return { success: true, data: { ...token } };
        } else {
          this.authService.logOut();
          return of({ ...response, success: false, ...errors });
        }
      }),
      catchError((err) => {
        const errors = this.dataService.getError(err);
        this.authService.logOut();
        return of({ success: false, ...err, errors });
      })
    );
  }

  sendVerificationEmail(userId: string, email: string = '') {
    const normalizedEmail = normalizeEmail(email);
    const headerMaps = new Map();
    headerMaps.set('skip', 'true');
    const requestParam: RequestParam = {
      url: normalizedEmail
        ? API_CONSTANTS.VERIFYEMAIL_BY_EMAIL(normalizedEmail)
        : API_CONSTANTS.VERIFYEMAIL(userId),
      params: { productType: this.store.getProduct() },
      headers: Object.fromEntries(headerMaps),
    };

    return this.dataService.get(requestParam).pipe(
      map((response) => {
        return { ...response.data, success: true };
      }),
      catchError((err) => {
        const errors = err?.error?.errors;
        const errorObj =
          Array.isArray(errors) && !!errors.length
            ? errors[0]
            : API_ERRORS.defaultError;
        return of({ ...err, success: false, ...errorObj });
      })
    );
  }

  forgotPassword(email: string) {
    try {
      const normalizedEmail = normalizeEmail(email);
      const headerMaps = new Map();
      headerMaps.set('skip', 'true');
      const requestParam: RequestParam = {
        url: API_CONSTANTS.FORGOTPASSWORD(normalizedEmail),
        params: { productType: this.store.getProduct() },
        headers: Object.fromEntries(headerMaps),
      };
      return this.dataService.get(requestParam).pipe(
        map((response) => {
          return { ...response.data, success: true };
        }),
        catchError((err) => {
          const errors = err?.error?.errors;
          const errorObj =
            Array.isArray(errors) && !!errors.length
              ? errors[0]
              : API_ERRORS.defaultError;
          return of({ ...err, success: false, ...errorObj });
        })
      );
    } catch (e) {
      console.log(e);
      return of(API_ERRORS.defaultError);
    }
  }

  checkLinkExpiry(userId: string, code: string) {
    try {
      const headerMaps = new Map();
      headerMaps.set('skip', 'true');
      const requestParam: RequestParam = {
        url: API_CONSTANTS.LINK_EXPIRY,
        params: { userId, code },
        headers: Object.fromEntries(headerMaps),
      };

      return this.dataService.get(requestParam).pipe(
        map((response) => {
          return { data: response.data, success: true };
        }),
        catchError((err) => {
          return of({
            ...err,
            success: false,
            errors: this.dataService.getError(err),
          });
        })
      );
    } catch (e) {
      console.log(e);
      return of(API_ERRORS.defaultError);
    }
  }

  /* API function to check if email is verified or not */
  isEmailVerified(email: string) {
    try {
      const normalizedEmail = normalizeEmail(email);
      const headerMaps = new Map();
      headerMaps.set('skip', 'true');
      const requestParam: RequestParam = {
        url: API_CONSTANTS.IS_EMAIL_VERIFIED,
        params: { email: normalizedEmail },
        headers: Object.fromEntries(headerMaps),
      };

      return this.dataService.get(requestParam).pipe(
        map((response) => {
          return { data: response.data, success: true };
        }),
        catchError((err) => {
          return of({
            ...err,
            success: false,
            errors: this.dataService.getError(err),
          });
        })
      );
    } catch (e) {
      console.log(e);
      return of(API_ERRORS.defaultError);
    }
  }

  getCustomToken(uid: string, name: string, email: string, roles: any) {
    try {
      if (!uid) {
        throw '';
      }
      const headers = new Map();
      headers.set('skip', 'true');
      const requestParam: RequestParam = {
        url: API_CONSTANTS.CUSTOM_TOKEN,
        data: { uid, name, email, roles },
        headers: Object.fromEntries(headers),
      };

      return this.dataService.post(requestParam).pipe(
        switchMap((response) => {
          if (response?.data) {
            this.store.setCustomToken(response.data);
            return of({ ...response, success: true });
          } else {
            const errors = this.dataService.getError(response);
            return of({ ...response, success: false, errors });
          }
        }),
        catchError((err) => {
          const errors = this.dataService.getError(err);
          return of({ ...err, success: false, errors });
        })
      );
    } catch (e) {
      console.error(e);
      return of({ ...(e || {}), errors: API_ERRORS.defaultError });
    }
  }

  saveUser(model: IUserInfo) {
    const user: IUserInfo = model || {};
    this.store.setUser(user);
  }

  getUserModel(queryMap: ParamMap): IUserInfo {
    const userInfo = this.store.getUser() || {};
    userInfo.userId =
      queryMap.get('id') || queryMap.get('userId') || queryMap.get('uid') || '';
    return userInfo;
  }
}
